<template>
    <div>
        <div class="modal fade" id="contact_agent_modal" tabindex="-1" aria-labelledby="contactAgentLabel"
            aria-hidden="true" data-bs-backdrop="false" style="background-color:rgba(0, 0, 0, 0.5);">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content contact-agent__modal"
                    :style="navBarStyle">
                    <div class="modal-header contact-agent__header" :style="navBarTextStyle" >
                        <h5 class="modal-title contact-agent__title" id="contactAgentLabel">Recargar</h5>
                        <button type="button" class="btn-close contact-agent__close" data-bs-dismiss="modal"
                            aria-label="Close"></button>
                    </div>
                    <div class="modal-body contact-agent__body" :style="navBarTextStyle"
                        style="background: #555858;" >
                        <p class="contact-agent__hint" v-if="cbuAvailable && !copyError">Toca para copiar</p>
                        <p class="contact-agent__hint contact-agent__hint--error" v-if="copyError">
                            No se pudo copiar automaticamente. Presiona Ctrl+C.
                        </p>
                        <div v-if="cbuAvailable" class="contact-agent__item" :class="{ 'is-copied': copied }"
                            role="button" tabindex="0"
                            @click="copyCbu" @keydown.enter="copyCbu" @keydown.space.prevent="copyCbu">
                            <div class="contact-agent__label">CBU</div>
                            <input ref="cbuInput" class="contact-agent__value contact-agent__value--input"
                                :value="cbuDisplay" readonly />
                            <div class="contact-agent__action">
                                <span>{{ copied ? 'Copiado' : 'Copiar' }}</span>
                            </div>
                        </div>
                        <p v-if="!cbuAvailable" class="contact-agent__hint">
                            CBU no disponible.
                        </p>
                        <div class="contact-agent__footer">
                            <button type="button" class="btn btn-sm contact-agent__dismiss" data-bs-dismiss="modal"
                                aria-label="Close">Cerrar</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "contactAgent",
    props: {
        skin: { type: Object, required: true },
        language: { type: String, default: "es" },
        translation: { type: Object, default: () => ({}) }
    },
    data() {
        return {
            copied: false,
            copyError: false,
            copyTimeout: null
        };
    },
    computed: {
        user() {
            return this.$store.getters["getUserData"];
        },
        cbuValue() {
            return (
                this.normalizeValue(this.user?.user_data?.data?.cuenta) ||
                this.normalizeValue(this.user?.user_data?.cuenta) ||
                this.normalizeValue(this.skin?.contact_agent?.cbu) ||
                ""
            );
        },
        cbuAvailable() {
            return this.cbuValue !== "";
        },
        cbuDisplay() {
            return this.cbuValue;
        },
        navBarStyle() {
            return this.pickStyle(this.skin?.header_desktop?.nav_bar, ["background", "background-color"]);
        },
        navBarTextStyle() {
            return this.pickStyle(this.skin?.header_desktop?.nav_bar_text, ["color"]);
        }
    },
    methods: {
        normalizeValue(value) {
            if (value === null || value === undefined) {
                return "";
            }
            return String(value).trim();
        },
        pickStyle(style, allowedKeys) {
            if (!style) {
                return {};
            }
            const allowed = new Set(allowedKeys.map((key) => key.toLowerCase()));
            const result = {};
            const assignIfAllowed = (key, value) => {
                const normalizedKey = this.normalizeStyleKey(key);
                if (!allowed.has(normalizedKey)) {
                    return;
                }
                const targetKey = this.styleKeyToCamel(normalizedKey);
                result[targetKey] = value;
            };

            if (typeof style === "string") {
                const output = [];
                style.split(";").forEach((declaration) => {
                    const [rawKey, ...rawValue] = declaration.split(":");
                    if (!rawKey || rawValue.length === 0) {
                        return;
                    }
                    const value = rawValue.join(":").trim();
                    if (!value) {
                        return;
                    }
                    const normalizedKey = this.normalizeStyleKey(rawKey.trim());
                    if (!allowed.has(normalizedKey)) {
                        return;
                    }
                    output.push(`${normalizedKey}: ${value}`);
                });
                return output.length ? `${output.join("; ")};` : {};
            }

            if (Array.isArray(style)) {
                const output = [];
                style.forEach((entry) => {
                    const sanitized = this.pickStyle(entry, allowedKeys);
                    if (!sanitized || (typeof sanitized === "object" && !Array.isArray(sanitized) && !Object.keys(sanitized).length)) {
                        return;
                    }
                    if (Array.isArray(sanitized)) {
                        output.push(...sanitized);
                        return;
                    }
                    output.push(sanitized);
                });
                if (!output.length) {
                    return {};
                }
                return output.length === 1 ? output[0] : output;
            }

            if (typeof style === "object") {
                Object.keys(style).forEach((key) => {
                    const value = style[key];
                    if (value === null || value === undefined || value === "") {
                        return;
                    }
                    assignIfAllowed(key, value);
                });
                return result;
            }

            return result;
        },
        normalizeStyleKey(key) {
            return String(key)
                .trim()
                .replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`)
                .toLowerCase();
        },
        styleKeyToCamel(key) {
            if (key === "background-color") {
                return "backgroundColor";
            }
            return key;
        },
        async copyCbu() {
            if (!this.cbuValue) {
                return;
            }
            this.selectCbu();
            const success = await this.copyText(this.cbuValue);
            this.copied = success;
            this.copyError = !success;
            if (this.copyTimeout) {
                clearTimeout(this.copyTimeout);
            }
            this.copyTimeout = setTimeout(() => {
                this.copied = false;
                this.copyError = false;
            }, 1500);
        },
        async copyText(value) {
            if (this.canUseClipboardApi()) {
                try {
                    await navigator.clipboard.writeText(value);
                    return true;
                } catch (error) {
                    // fallback below
                }
            }
            return this.copyWithExecCommand(value);
        },
        canUseClipboardApi() {
            if (!navigator.clipboard || !window.isSecureContext) {
                return false;
            }
            const policy = document.permissionsPolicy || document.featurePolicy;
            if (policy && typeof policy.allowsFeature === "function") {
                return policy.allowsFeature("clipboard-write");
            }
            return true;
        },
        copyWithExecCommand(value) {
            const input = this.$refs.cbuInput;
            if (input) {
                try {
                    input.focus();
                    input.select();
                    return document.execCommand("copy");
                } catch (error) {
                    // fallback below
                }
            }
            try {
                const textarea = document.createElement("textarea");
                textarea.value = value;
                textarea.setAttribute("readonly", "");
                textarea.style.position = "absolute";
                textarea.style.left = "-9999px";
                document.body.appendChild(textarea);
                textarea.select();
                const success = document.execCommand("copy");
                document.body.removeChild(textarea);
                return success;
            } catch (error) {
                return false;
            }
        },
        selectCbu() {
            const input = this.$refs.cbuInput;
            if (!input) {
                return;
            }
            input.focus();
            input.select();
        },
        openModal() {
            const modalEl = document.getElementById("contact_agent_modal");
            if (!modalEl) {
                return;
            }
            if (typeof bootstrap !== "undefined" && bootstrap.Modal) {
                const instance = bootstrap.Modal.getOrCreateInstance(modalEl);
                instance.show();
            }
        }
    },
    mounted() {
        this.$root.$on("openContactAgent", this.openModal);
    },
    beforeDestroy() {
        this.$root.$off("openContactAgent", this.openModal);
        if (this.copyTimeout) {
            clearTimeout(this.copyTimeout);
        }
    }
};
</script>

<style scoped>
.contact-agent__modal {
    position: relative;
    border-radius: 18px;
    overflow: hidden;
}

.contact-agent__header {
    background: transparent;
    border-bottom: 1px solid currentColor;
    padding: 14px 18px;
    position: relative;
}

.contact-agent__title {
    font-weight: 700;
    font-size: 16px;
    letter-spacing: 0.2px;
}

.contact-agent__close {
    position: relative;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    background: transparent;
    background-image: none;
    color: inherit;
    opacity: 1;
}

.contact-agent__close::before {
    content: "X";
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    color: currentColor;
}

.contact-agent__body {
    padding: 18px;
}

.contact-agent__hint {
    margin: 0 0 12px 0;
    font-size: 12px;
    opacity: 0.7;
}

.contact-agent__hint--error {
    color: inherit;
}

.contact-agent__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 14px;
    background: transparent;
    border: 1px solid currentColor;
    cursor: pointer;
    margin-bottom: 10px;
    box-shadow: none;
    transition: background 0.2s ease, border 0.2s ease, box-shadow 0.2s ease,
        transform 0.2s ease;
}

.contact-agent__item:hover {
    background: transparent;
    box-shadow: none;
    transform: translateY(-1px);
}

.contact-agent__item.is-copied {
    border-color: currentColor;
    background: transparent;
}

.contact-agent__item.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.contact-agent__label {
    font-weight: 700;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    min-width: 80px;
}

.contact-agent__value {
    flex: 1;
    font-size: 13px;
    word-break: break-all;
    color: inherit;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
        "Courier New", monospace;
}

.contact-agent__value--input {
    border: none;
    background: transparent;
    padding: 0;
    outline: none;
    color: inherit;
}

.contact-agent__value--input::selection {
    background: transparent;
}

.contact-agent__action {
    font-size: 12px;
    font-weight: 700;
    color: inherit;
    background: transparent;
    border: 1px solid currentColor;
    padding: 6px 10px;
    border-radius: 999px;
    min-width: 72px;
    text-align: center;
    transition: background 0.2s ease, border 0.2s ease, color 0.2s ease;
}

.contact-agent__item.is-copied .contact-agent__action {
    background: transparent;
    border-color: currentColor;
}

.contact-agent__footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 8px;
}

.contact-agent__dismiss {
    background: transparent;
    color: inherit;
    border: 1px solid currentColor;
    border-radius: 999px;
    font-weight: 600;
    padding: 6px 14px;
    transition: background 0.2s ease, border 0.2s ease;
}

.contact-agent__dismiss:hover {
    background: transparent;
}

@media (max-width: 480px) {
    .contact-agent__header {
        padding: 12px 14px;
    }

    .contact-agent__body {
        padding: 14px;
    }

    .contact-agent__item {
        padding: 10px 12px;
    }

    .contact-agent__value {
        font-size: 12px;
    }
}
</style>
